/** XeAgents.ai**/
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Info, Lightbulb, RefreshCw, ScanSearch } from "lucide-react";
import type { ExtractionStage, ScriptLine } from "./types";

/**
 * What the read found, as a running checklist.
 *
 * Before the read it is the page's answer to "what will this do for me"; after
 * it, the receipt. Either way it is the same list, which is why it is one card
 * with a state rather than two.
 */
export function ExtractionStatus({
  stage,
  onReExtract,
}: {
  stage: ExtractionStage;
  onReExtract: () => void;
}) {
  const done = stage.status === 'done';
  const items = [
    { label: "Website analyzed", complete: done },
    { label: "Company information", complete: done },
    { label: "Product details", complete: done },
    { label: "Unique value proposition", complete: done },
    { label: "Key features", complete: done },
    { label: "Common objections", complete: done },
  ];

  return (
    <Card className={done ? "border-emerald-200 dark:border-emerald-900/50" : "border-border/70"}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <ScanSearch className="h-4 w-4 text-primary" />
          AI Extraction
          {done && (
            <Badge
              variant="outline"
              className="ml-auto h-5 border-emerald-300 px-1.5 text-[10px] font-normal text-emerald-700 dark:text-emerald-300"
            >
              Complete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li
              key={item.label}
              className={`flex items-center gap-2 text-xs ${item.complete ? "text-foreground" : "text-muted-foreground"}`}
            >
              {item.complete ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Circle className="h-3.5 w-3.5 shrink-0 opacity-40" />
              )}
              {item.label}
            </li>
          ))}
        </ul>

        {done ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onReExtract}
            data-testid="button-sidebar-reextract"
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Re-extract
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground leading-snug">
            Add your website address in Step 2 and we'll fill most of this page for you.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Placeholders read as noise here; show the shape of the sentence instead. */
function readable(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const filled = String(values[key] ?? "").trim();
    return filled || `[${String(key).replace(/_/g, " ")}]`;
  });
}

/**
 * The script as it stands, read back.
 *
 * Static on purpose. The page's job is to get the words right, and the fastest
 * way to judge words is to read them next to the fields that produced them -
 * not to place a call, which is a different task on a different page.
 */
export function TemplatePreview({
  name,
  openingLine,
  scriptPreview,
  values,
}: {
  name: string;
  openingLine?: string;
  scriptPreview?: ScriptLine[];
  values: Record<string, string>;
}) {
  const lines = (scriptPreview?.length ? scriptPreview : openingLine ? [{ label: "Opening", message: openingLine }] : []);

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Template Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm font-medium text-foreground" data-testid="text-preview-name">
          {name?.trim() || "Untitled template"}
        </p>

        {lines.length > 0 ? (
          <div className="max-h-[42vh] space-y-3 overflow-y-auto pr-1">
            {lines.map((line, index) => (
              <div key={`${line.label}-${index}`} className="space-y-1">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{line.label}</p>
                <blockquote className="border-l-2 border-primary/25 pl-3 text-xs leading-relaxed text-foreground/90">
                  {readable(line.message, values)}
                </blockquote>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Fill in the details and the script will appear here.</p>
        )}

        <p className="flex items-start gap-1.5 border-t pt-3 text-[11px] text-muted-foreground leading-snug">
          <Info className="mt-px h-3 w-3 shrink-0" />
          The preview shows the template wording with your values filled in. Key features and objections are applied when the flow is created, so they do not appear here.
        </p>
      </CardContent>
    </Card>
  );
}

const TIPS = [
  "Use a specific product URL for better extraction",
  "Review AI-generated content before saving",
  "Keep your value proposition concise",
  "Include common customer objections",
];

export function TemplateTips() {
  return (
    <Card className="border-border/70 bg-primary/[0.03]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Lightbulb className="h-4 w-4 text-primary" />
          Tips
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {TIPS.map((tip) => (
            <li key={tip} className="flex items-start gap-2 text-xs text-muted-foreground leading-snug">
              <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-primary/70" />
              {tip}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
